import { defineStore } from 'pinia';
import type { Ref } from 'vue';
import { computed, ref } from 'vue';

import type {
  MapDisplaySettingsDTO,
  PublicSettingsDTO,
  SettingDTO,
  UpdateSettingDTO,
  UserSettingsResponseDTO,
} from '@nora-monorepo/nora-api-client';
import { SettingKey } from '@nora-monorepo/nora-api-client';

import { useApiStore } from '#ui-shared/stores/api/api-store';
import { MapDisplaySettings } from '#ui-shared/types/MapDisplaySettings';

type NestedKeys<T> = {
  [K in keyof T & (string | number)]: T[K] extends Array<unknown>
    ? `${K}` | `${K}[${number}]`
    : T[K] extends object
      ? `${K}` | `${K}.${NestedKeys<T[K]>}`
      : `${K}`;
}[keyof T & (string | number)];

type ValueAtPath<T, P extends string> = P extends `${infer K}.${infer Rest}`
  ? K extends keyof T
    ? ValueAtPath<T[K], Rest>
    : never
  : P extends keyof T
    ? T[P]
    : never;

export const useConfigStore = defineStore('configStore', () => {
  const BASE_URL = ref('');
  const publicConfig = ref<PublicSettingsDTO>();
  const appConfig = ref<SettingDTO>();
  const userSettings = ref<UserSettingsResponseDTO>({});
  const userMapDisplaySettings = computed(() => {
    return MapDisplaySettings.fromMapSettingsDTOOrDefault(getUserSetting(SettingKey.MAP_DISPLAY));
  });

  const api = useApiStore();

  const baseUrl = computed(() => BASE_URL.value);

  function setBaseUrl(url: string): void {
    BASE_URL.value = url;

    api.updateBasePath(url);
  }

  function setAppConfig(config: SettingDTO): void {
    appConfig.value = config;
  }

  function setPublicConfig(config: PublicSettingsDTO): void {
    publicConfig.value = config;
  }

  function setUserSettings(config: UserSettingsResponseDTO): void {
    userSettings.value = config;
  }

  function setUserSetting<K extends keyof UserSettingsResponseDTO>(
    key: K,
    value: UserSettingsResponseDTO[K],
  ): void {
    userSettings.value[key] = value;
  }

  /**
   * Updates the Map Display Settings for the user, persisting this change on the
   * Nora API.
   *
   * This should be used when the origin of the settings change is the current device. If the settings change is from an SSE event, {@link eventUpdateMapDisplaySettings} should be used instead.
   *
   * These settings affect the information in the bottom right hand side of the map. As of May
   * 2026, this includes the different coordinate formats, and the scale line.
   * @param value
   */
  async function updateUserMapDisplaySettings(value: MapDisplaySettingsDTO): Promise<void> {
    await api.settings.updateMapDisplaySettings({
      mapDisplaySettingsDTO: value,
    });

    userSettings.value.MAP_DISPLAY = value;
  }

  /**
   * Updates the Map Display Settings for the user without making an API call. This is useful
   * when updating state after receiving an SSE event.
   *
   * We listen to the map settings SSE event because we want as much consistency across devices
   * as possible. That means that whenever a user changes the settings on mobile, they should be
   * reflected on web, and vice versa.
   *
   * Note that this will not guarantee consistency across devices all the time. For example, if a
   * device is offline - or otherwise operate in a degraded network environment - that event
   * will or may never reach that device.
   * @param value
   */
  function eventUpdateMapDisplaySettings(value: MapDisplaySettingsDTO): void {
    userSettings.value.MAP_DISPLAY = value;
  }

  /**
   * This handler is called when we have received an SSE event for updated user settings.
   *
   * Note that, while Map Display Settings are formally part of user settings, they should be
   * handled more specifically in {@link eventUpdateMapDisplaySettings}.
   * @param settingsPayload
   */
  async function eventUpdateUserSettings(settingsPayload: UpdateSettingDTO): Promise<void> {
    const key: keyof UserSettingsResponseDTO = settingsPayload.key;
    const value = settingsPayload.value as UserSettingsResponseDTO[typeof key];

    if (!userSettings.value || !key || value === undefined) {
      return;
    }

    setUserSetting(key, value);
  }

  // Get method which allows `.` notation
  function get<
    P extends NestedKeys<SettingDTO>,
    R extends ValueAtPath<SettingDTO, P> | undefined,
  >(path: P, defaultValue?: R): R {
    if (!appConfig.value) {
      return defaultValue as R;
    }

    const keys = path.split('.') as string[];

    let result: unknown = appConfig.value;

    for (const key of keys) {
      if (result === null || typeof result !== 'object') {
        return defaultValue as R;
      }

      result = (result as Record<string, unknown>)[key];
    }

    return (result ?? defaultValue) as R;
  }

  function getUserSetting<K extends keyof UserSettingsResponseDTO>(
    key: K,
    fallback?: UserSettingsResponseDTO[K],
  ): UserSettingsResponseDTO[K] | undefined {
    if (!userSettings.value) {
      return fallback;
    }

    return userSettings.value[key];
  }

  function getUserMapSettingsRef(): Ref<MapDisplaySettings> {
    return userMapDisplaySettings;
  }

  async function updateUserSetting<K extends keyof UserSettingsResponseDTO>(
    key: K,
    value: UserSettingsResponseDTO[K],
  ): Promise<void> {
    if (!userSettings.value || value === undefined) {
      return;
    }

    userSettings.value[key] = value;

    await api.settings.updateUserSetting({
      updateSettingDTO: {
        key,
        value,
      },
    });
  }

  /**
   * Persists a global setting and stores the value as saved by the server, which drops unknown fields.
   */
  async function updateGlobalSetting<K extends keyof UserSettingsResponseDTO>(
    key: K,
    value: UserSettingsResponseDTO[K],
  ): Promise<UserSettingsResponseDTO[K] | undefined> {
    if (value === undefined) {
      return undefined;
    }

    const response = await api.settings.updateGlobalSetting({
      updateSettingDTO: {
        key,
        value,
      },
    });

    const savedValue = (response.data.data?.value ?? value) as UserSettingsResponseDTO[K];

    if (userSettings.value) {
      userSettings.value[key] = savedValue;
    }

    return savedValue;
  }

  return {
    baseUrl,
    appConfig,
    publicConfig,
    userSettings,
    eventUpdateMapDisplaySettings,
    eventUpdateUserSettings,
    setBaseUrl,
    setAppConfig,
    setPublicConfig,
    setUserSettings,
    updateUserMapDisplaySettings,
    getUserMapSettingsRef,
    getUserSetting,
    updateUserSetting,
    updateGlobalSetting,
    get,
  };
});
